// The backend stores address as one combined string (e.g. "Carnapstraat 53" or, number-first,
// "10 Downing Street") - these split it apart for display and recombine it for saving.

// The number part also allows a hyphenated addition (e.g. "12-2" or "12A-bis") - PDOK returns
// house numbers in that form for some Dutch addresses.
const TRAILING_NUMBER = /^(.*?)\s+(\d+[a-zA-Z]*(?:-[a-zA-Z0-9]+)?)$/;
const LEADING_NUMBER = /^(\d+[a-zA-Z]*(?:-[a-zA-Z0-9]+)?)\s+(.*)$/;

export interface SplitAddress {
  street: string;
  houseNumber: string;
  numberFirst: boolean;
}

export const splitAddress = (
  value: string,
  previousNumberFirst: boolean,
): SplitAddress => {
  const trailingMatch = value?.match(TRAILING_NUMBER);

  if (trailingMatch) {
    return {
      street: trailingMatch[1],
      houseNumber: trailingMatch[2],
      numberFirst: false,
    };
  }

  const leadingMatch = value?.match(LEADING_NUMBER);

  if (leadingMatch) {
    return {
      street: leadingMatch[2],
      houseNumber: leadingMatch[1],
      numberFirst: true,
    };
  }

  // No number in the value at all - numberFirst is left as-is rather than reset, since it
  // otherwise can't be known until a house number reappears.
  return {
    street: value ?? '',
    houseNumber: '',
    numberFirst: previousNumberFirst,
  };
};

// Avoids duplicating the house number if it's already part of a hand-edited street value.
export const combineAddress = (
  street: string,
  houseNumber: string,
  numberFirst: boolean,
): string => {
  if (!houseNumber) {
    return street;
  }

  const trimmedStreet = street.trim();

  if (numberFirst) {
    if (
      trimmedStreet === houseNumber ||
      trimmedStreet.startsWith(`${houseNumber} `)
    ) {
      return trimmedStreet;
    }

    return `${houseNumber} ${trimmedStreet}`.trim();
  }

  if (
    trimmedStreet === houseNumber ||
    trimmedStreet.endsWith(` ${houseNumber}`)
  ) {
    return trimmedStreet;
  }

  return `${trimmedStreet} ${houseNumber}`.trim();
};
